import {
  ImplementationDetail,
  ProviderConfig,
} from '@finch-api/common/dist/internal/connect/validate';
import {
  ERRORS_A_DIFFERENT_USER_NEEDS_TO_AUTHENTICATE,
  ERRORS_USERS_CAN_RESOLVE_WITHIN_CONNECT,
  ERRORS_USERS_NEEDS_TO_LEAVE_CONNECT_TO_SOLVE,
  FinchErrorCode,
} from 'constants/finch-error-code';
import { shouldTriggerAuthFallback } from 'lib/auth-fallback/utils';
import { useAuthFailureCount } from 'store/auth-failure-count';
import { useCameFromAuthFallback } from 'store/came-from-auth-fallback';
import { useAuthorizeContext } from '../AuthorizeContext';
import { AuthFallbackThresholdError } from '../Errors/AuthFallbackThresholdError';
import { isUnexpectedError } from '../AuthorizeProvider';
import { CriticalAuthError } from '../Errors/CriticalAuthError';
import { step } from '../steps';
import { ConnectError } from '../types';
import { Box } from '@chakra-ui/react';
import Notifications from 'components/Notifications/Notifications';
import { useEffect, useRef } from 'react';
import { getClientDisplayNameInSentence } from 'utils/connect-branding';

const GLOBAL_UNEXPECTED_ERROR_COUNT_THRESHOLD = 5;

const StickyErrorBanner = ({
  error,
  setError,
}: {
  error: ConnectError;
  setError: (error: ConnectError | null) => void;
}) => (
  <Box width="100%" position="sticky" top="0" my="2">
    <Notifications error={error} setError={setError} />
  </Box>
);

export const ProviderAppShellError = ({
  error,
  setError,
  providerImplementation,
  instructionsLink,
  provider,
}: {
  provider: ProviderConfig;
  providerImplementation: ImplementationDetail;
  setError: (error: ConnectError | null) => void;
  error: ConnectError;
  instructionsLink?: string;
}) => {
  const { client, currentStep, setCurrentStep } = useAuthorizeContext() || {};

  const authFailureCounts = useAuthFailureCount(
    (state) => state.authFailureCounts,
  );

  const setCameFromAuthFallback = useCameFromAuthFallback(
    (state) => state.setCameFromAuthFallback,
  );

  // Passkey-only / no-usable-MFA QuickBooks accounts (and any future provider
  // that surfaces this hint) can never complete standard auth, so we route the
  // employer straight to the auth fallback (manual sign in) and keep the banner
  // visible to explain why. Gated exclusively by the finch_code we emit.
  const shouldRedirectToAuthFallback =
    error?.finchCode === FinchErrorCode.RedirectToAuthFallback;
  const hasRedirectedToAuthFallback = useRef(false);

  useEffect(() => {
    if (!shouldRedirectToAuthFallback) {
      // Reset the guard so a later RedirectToAuthFallback error still triggers a
      // redirect, even if this instance stays mounted across different errors.
      hasRedirectedToAuthFallback.current = false;
      return;
    }
    if (!hasRedirectedToAuthFallback.current) {
      hasRedirectedToAuthFallback.current = true;
      // Mark the flow as originating from auth fallback so the downstream
      // manual sign-in POST sends acfFallbackEligible=true. Without this,
      // api-server mints a fully assisted (manual=true) token instead of an
      // automated auth-fallback (manual=false) one.
      setCameFromAuthFallback(true);
      setCurrentStep?.(step.MANUAL_SIGN_IN);
    }
  }, [shouldRedirectToAuthFallback, setCurrentStep, setCameFromAuthFallback]);

  const authFallbackThresholdReached = shouldTriggerAuthFallback(
    client,
    authFailureCounts,
    providerImplementation,
  );

  const currentProviderErrorCount =
    authFailureCounts[providerImplementation.id];

  const userNeedsToLeaveConnect =
    error?.finchCode &&
    ERRORS_USERS_NEEDS_TO_LEAVE_CONNECT_TO_SOLVE.includes(error.finchCode);

  const aDifferentUserNeedsToAuthenticate =
    error?.finchCode &&
    ERRORS_A_DIFFERENT_USER_NEEDS_TO_AUTHENTICATE.includes(error.finchCode);

  const userCanResolveError =
    error?.finchCode &&
    ERRORS_USERS_CAN_RESOLVE_WITHIN_CONNECT.includes(error.finchCode);

  if (shouldRedirectToAuthFallback) {
    return (
      <StickyErrorBanner
        error={{
          ...error,
          formattedMessage: undefined,
          message: `Your ${provider.displayName} account requires a verification method we can't complete automatically. Please connect manually to continue.`,
        }}
        setError={setError}
      />
    );
  }

  if (userNeedsToLeaveConnect) {
    return (
      <CriticalAuthError
        errorMessage={error.message}
        formattedMessage={error.formattedMessage}
        onButtonClick={() => setError(null)}
        buttonLabel="Try again"
      />
    );
  }

  if (aDifferentUserNeedsToAuthenticate) {
    return (
      <CriticalAuthError
        errorMessage={error.message}
        formattedMessage={error.formattedMessage}
        instructionsLink={instructionsLink}
      />
    );
  }

  if (isUnexpectedError(error)) {
    if (authFallbackThresholdReached) {
      return (
        <AuthFallbackThresholdError
          providerName={provider.displayName}
          onNext={() => {
            setError?.(null);
            setCurrentStep?.(step.MANUAL_SIGN_IN);
          }}
        />
      );
    }

    if (currentProviderErrorCount >= GLOBAL_UNEXPECTED_ERROR_COUNT_THRESHOLD) {
      return (
        <CriticalAuthError
          headerText="Please try again later"
          errorMessage={`We're currently experiencing connection issues with ${
            provider.displayName
          }. Please reach out to ${getClientDisplayNameInSentence(
            client,
          )} for more assistance. Or you can come back later to try again.`}
        />
      );
    }

    if (currentStep && [step.MFA, step.CAPTCHA].includes(currentStep)) {
      return (
        <CriticalAuthError
          headerText="An unexpected error has occurred"
          buttonLabel="Back to Sign In"
          errorMessage="Please restart the authentication process"
          onButtonClick={() => {
            setError(null);
            setCurrentStep?.(step.SIGN_IN);
          }}
        />
      );
    }

    // If we specify a finchCode, we can show a more specific error message
    if (error?.finchCode) {
      return (
        <CriticalAuthError
          buttonLabel="Try again"
          errorMessage={error.message}
          formattedMessage={error.formattedMessage}
          onButtonClick={() => setError?.(null)}
        />
      );
    }

    return (
      <CriticalAuthError
        buttonLabel="Try again"
        onButtonClick={() => setError?.(null)}
      />
    );
  }

  if (userCanResolveError) {
    return <StickyErrorBanner error={error} setError={setError} />;
  }

  return (
    <CriticalAuthError
      buttonLabel="Try again"
      errorMessage={error.message}
      formattedMessage={error.formattedMessage}
      onButtonClick={() => setError?.(null)}
    />
  );
};
